import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";

const ROLE_HEADER = "X-Admin-Role";
const SUPER_ADMIN = "SUPER_ADMIN";

const EMPTY_FORM = {
  id: null,
  code: "",
  title: "",
  description: "",
  discountAmount: 500,
  minBookingAmount: 0,
  popupDefault: false,
  active: true,
  promoSource: "OTHER",
  startDate: "",
  endDate: "",
};

function toIsoDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Format an ISO/Date value as the local-time string `<input type="datetime-local">` expects. */
function dateForInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const PROMO_SOURCES = [
  { value: "OTHER", label: "General / other" },
  { value: "CUSTOMER_POPUP", label: "Customer popup" },
  { value: "INSTAGRAM", label: "Instagram" },
  { value: "YOUTUBE", label: "YouTube" },
  { value: "GOOGLE_LINK", label: "Google link / ads" },
  { value: "META", label: "Meta (Facebook)" },
  { value: "WHATSAPP", label: "WhatsApp" },
];

function labelForPromoSource(code) {
  const row = PROMO_SOURCES.find((s) => s.value === code);
  return row ? row.label : code || "—";
}

/**
 * Super-admin only: manage promo codes (create / edit / activate-deactivate / delete) and review
 * usage stats for each code (lead count, redemption count, total discount given, recent rows).
 */
export default function AdminPromoCodes() {
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [usageOpenFor, setUsageOpenFor] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [welcomeFlashEnabled, setWelcomeFlashEnabled] = useState(true);
  const [welcomeFlashSaving, setWelcomeFlashSaving] = useState(false);

  const [slotOfferBookings, setSlotOfferBookings] = useState([]);
  const [slotOfferBookingsLoading, setSlotOfferBookingsLoading] = useState(false);

  const role = String(context?.adminRole || "").toUpperCase();

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (role !== SUPER_ADMIN) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, role, navigate]);

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 5000);
  }, []);

  const headers = useMemo(
    () => ({
      [ROLE_HEADER]: role,
      "X-Admin-User": context?.adminUserName || "",
    }),
    [role, context?.adminUserName]
  );

  const popupPromoRows = useMemo(
    () =>
      rows.filter((r) => String(r.promoSource || "").toUpperCase() === "CUSTOMER_POPUP"),
    [rows]
  );

  const loadList = useCallback(() => {
    setLoading(true);
    axios
      .get(Constants.ADMIN_PROMO_CODES, { headers })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        const msg = err?.response?.data?.message || "Could not load promo codes.";
        showBanner("danger", msg);
      })
      .finally(() => setLoading(false));
  }, [headers, showBanner]);

  const loadSlotOfferBookings = useCallback(() => {
    setSlotOfferBookingsLoading(true);
    axios
      .get(Constants.ADMIN_PROMO_CODES_SLOT_TIME_OFFER_BOOKINGS, {
        headers,
        params: { limit: 200 },
      })
      .then((res) =>
        setSlotOfferBookings(Array.isArray(res.data) ? res.data : [])
      )
      .catch(() => {
        showBanner("danger", "Could not load slot-time offer bookings.");
      })
      .finally(() => setSlotOfferBookingsLoading(false));
  }, [headers, showBanner]);

  useEffect(() => {
    if (role === SUPER_ADMIN) loadList();
  }, [role, loadList]);

  useEffect(() => {
    if (role === SUPER_ADMIN) loadSlotOfferBookings();
  }, [role, loadSlotOfferBookings]);

  // Load the welcome flash toggle once on mount.
  useEffect(() => {
    if (role !== SUPER_ADMIN) return;
    let cancelled = false;
    axios
      .get(Constants.SITE_WELCOME_FLASH)
      .then((res) => {
        if (!cancelled) setWelcomeFlashEnabled(res?.data?.enabled !== false);
      })
      .catch(() => {
        /* default true */
      });
    return () => {
      cancelled = true;
    };
  }, [role]);

  const onToggleWelcomeFlash = async (next) => {
    setWelcomeFlashSaving(true);
    try {
      await axios.put(
        Constants.ADMIN_WELCOME_FLASH,
        { enabled: next },
        { headers }
      );
      setWelcomeFlashEnabled(next);
      showBanner(
        "success",
        `Welcome flash ${next ? "turned on" : "turned off"}.`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not update setting.";
      showBanner("danger", msg);
    } finally {
      setWelcomeFlashSaving(false);
    }
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setShowFormModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      code: row.code || "",
      title: row.title || "",
      description: row.description || "",
      discountAmount: row.discountAmount || 0,
      minBookingAmount: row.minBookingAmount || 0,
      popupDefault: Boolean(row.popupDefault),
      active: Boolean(row.active),
      promoSource: row.promoSource ? String(row.promoSource).toUpperCase() : "OTHER",
      startDate: dateForInput(row.startDate),
      endDate: dateForInput(row.endDate),
    });
    setShowFormModal(true);
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    if (!form.code.trim()) {
      showBanner("warning", "Code is required.");
      return;
    }
    if (!form.discountAmount || Number(form.discountAmount) <= 0) {
      showBanner("warning", "Discount amount must be a positive number.");
      return;
    }
    setSubmitting(true);
    const payload = {
      id: form.id,
      code: form.code.trim().toUpperCase(),
      title: form.title.trim() || null,
      description: form.description.trim() || null,
      discountAmount: Number(form.discountAmount),
      minBookingAmount: Number(form.minBookingAmount) || 0,
      popupDefault: Boolean(form.popupDefault),
      active: Boolean(form.active),
      promoSource: form.promoSource || "OTHER",
      startDate: toIsoDate(form.startDate),
      endDate: toIsoDate(form.endDate),
    };
    try {
      if (form.id) {
        await axios.put(Constants.adminPromoCodeUrl(form.id), payload, { headers });
        showBanner("success", "Promo code updated.");
      } else {
        await axios.post(Constants.ADMIN_PROMO_CODES, payload, { headers });
        showBanner("success", "Promo code created.");
      }
      setShowFormModal(false);
      loadList();
    } catch (err) {
      const msg = err?.response?.data?.message || "Save failed.";
      showBanner("danger", msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(
        Constants.adminPromoCodeActiveUrl(row.id),
        { active: !row.active },
        { headers }
      );
      loadList();
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not update status.";
      showBanner("danger", msg);
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(Constants.adminPromoCodeUrl(confirmDelete.id), { headers });
      setConfirmDelete(null);
      showBanner("success", "Promo code deleted.");
      loadList();
    } catch (err) {
      const msg = err?.response?.data?.message || "Delete failed.";
      showBanner("danger", msg);
    }
  };

  const openUsage = (row) => {
    setUsageOpenFor(row);
    setUsage(null);
    setUsageLoading(true);
    axios
      .get(Constants.adminPromoCodeUsageUrl(row.id), { headers })
      .then((res) => setUsage(res.data))
      .catch((err) => {
        const msg = err?.response?.data?.message || "Could not load usage.";
        showBanner("danger", msg);
        setUsageOpenFor(null);
      })
      .finally(() => setUsageLoading(false));
  };

  return (
    <AdminLayout title="Promo codes">
      <div className="admin-console-grid-wrap" style={{ padding: 16 }}>
        <div className="admin-console-header" style={{ position: "static", marginBottom: 16 }}>
          <div className="admin-console-header-inner" style={{ justifyContent: "space-between" }}>
            <div>
              <p className="admin-content-eyebrow">Customer offers</p>
              <h1 className="admin-console-title" style={{ margin: 0 }}>
                Promo codes
              </h1>
              <p className="admin-gallery-intro" style={{ marginTop: 4 }}>
                Create flat-amount discount codes for checkout and campaigns. Assign a{' '}
                <strong>source</strong> (Instagram, WhatsApp, etc.). Only one{' '}
                <strong>Customer popup</strong> code can be <strong>active</strong> at a time —
                activating another will deactivate the rest. Codes can be used at most once per
                phone number.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  loadList();
                  loadSlotOfferBookings();
                }}
                disabled={loading || slotOfferBookingsLoading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : "Refresh"}
              </Button>
              <Button variant="primary" onClick={openNew}>
                <i className="bi bi-plus-lg" /> New code
              </Button>
            </div>
          </div>
        </div>

        {banner ? (
          <Alert variant={banner.variant} onClose={() => setBanner(null)} dismissible>
            {banner.message}
          </Alert>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            padding: "12px 14px",
            marginBottom: 14,
            border: "1px solid rgba(255, 209, 102, 0.35)",
            borderRadius: 12,
            background:
              "linear-gradient(92deg, rgba(255, 209, 102, 0.06), rgba(168, 85, 247, 0.05))",
          }}
        >
          <div>
            <div
              className="text-uppercase"
              style={{
                fontSize: 11,
                color: "#ffd166",
                letterSpacing: "0.16em",
                fontWeight: 700,
              }}
            >
              Welcome flash overlay
            </div>
            <div style={{ color: "#cdd5e1", fontSize: 13, marginTop: 2 }}>
              When ON, the splash screen plays for ~6 s on the home page before the
              promo popup appears. Turn OFF to skip the splash and show the promo
              popup immediately.
            </div>
          </div>
          <Form.Check
            type="switch"
            id="pb-welcome-flash-toggle"
            label={welcomeFlashEnabled ? "On" : "Off"}
            checked={welcomeFlashEnabled}
            disabled={welcomeFlashSaving}
            onChange={(e) => onToggleWelcomeFlash(e.target.checked)}
          />
        </div>

        <section className="mb-4">
          <div
            className="text-uppercase mb-2"
            style={{
              fontSize: 11,
              color: "#ffd166",
              letterSpacing: "0.16em",
              fontWeight: 700,
            }}
          >
            Customer popup promos only
          </div>
          <p className="text-muted small mb-2">
            The welcome popup pulls the active <code>CUSTOMER_POPUP</code> code (fallback: legacy “popup
            default” flag). Use <strong>New code</strong> or edit rows below — only one stays active when you
            save or toggle on.
          </p>
          <Table responsive hover size="sm" className="text-light" variant="dark">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Discount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {popupPromoRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted" style={{ padding: 14 }}>
                    No rows with source <strong>Customer popup</strong> yet — create one in{" "}
                    <strong>New code</strong>.
                  </td>
                </tr>
              ) : null}
              {popupPromoRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <code style={{ color: "#ffd166" }}>{row.code}</code>
                  </td>
                  <td>{row.title || "—"}</td>
                  <td>Rs. {row.discountAmount}</td>
                  <td>
                    <Badge bg={row.active ? "success" : "secondary"}>
                      {row.active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>

        <Table responsive hover size="sm" className="text-light" variant="dark">
          <thead>
            <tr>
              <th>Code</th>
              <th>Title</th>
              <th>Discount</th>
              <th>Min order</th>
              <th>Source</th>
              <th>Status</th>
              <th>Popup default</th>
              <th>Validity</th>
              <th className="text-end">Used</th>
              <th className="text-end">Discount given</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={11} className="text-center text-muted" style={{ padding: 24 }}>
                  No promo codes yet. Click <strong>New code</strong> to add one.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <code style={{ color: "#ffd166" }}>{row.code}</code>
                </td>
                <td>{row.title || "—"}</td>
                <td>Rs. {row.discountAmount}</td>
                <td>{row.minBookingAmount ? `Rs. ${row.minBookingAmount}` : "—"}</td>
                <td>{labelForPromoSource(row.promoSource)}</td>
                <td>
                  <Badge bg={row.active ? "success" : "secondary"}>
                    {row.active ? "Active" : "Inactive"}
                  </Badge>
                </td>
                <td>{row.popupDefault ? <Badge bg="info">Popup</Badge> : "—"}</td>
                <td style={{ minWidth: 170 }}>
                  <div style={{ fontSize: 12 }}>
                    From: {formatDateTime(row.startDate)}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    To: {formatDateTime(row.endDate)}
                  </div>
                </td>
                <td className="text-end">{row.totalRedemptions ?? 0}</td>
                <td className="text-end">Rs. {row.totalDiscountGiven ?? 0}</td>
                <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                  <Button size="sm" variant="outline-light" onClick={() => openUsage(row)}>
                    View usage
                  </Button>{" "}
                  <Button size="sm" variant="outline-light" onClick={() => openEdit(row)}>
                    Edit
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant={row.active ? "outline-warning" : "outline-success"}
                    onClick={() => toggleActive(row)}
                  >
                    {row.active ? "Deactivate" : "Activate"}
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setConfirmDelete(row)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <section className="mt-5">
          <div
            className="text-uppercase mb-2"
            style={{
              fontSize: 11,
              color: "#ffd166",
              letterSpacing: "0.16em",
              fontWeight: 700,
            }}
          >
            Slot-time offer usage (bookings)
          </div>
          <p className="text-muted small mb-2">
            Date-range slot offers from Theatre gallery — each row is a completed booking that received the
            promotional package base. <strong>Package discount</strong> is the saving on the listed slot base
            (not add-ons). Refresh updates this list.
          </p>
          {slotOfferBookingsLoading ? (
            <Spinner size="sm" animation="border" className="mb-2" />
          ) : null}
          <Table responsive hover size="sm" className="text-light" variant="dark">
            <thead>
              <tr>
                <th>Booking ref</th>
                <th>Date</th>
                <th>Theatre</th>
                <th>Slot</th>
                <th>Offer id</th>
                <th className="text-end">Listed ₹</th>
                <th className="text-end">Paid base ₹</th>
                <th className="text-end">Discount ₹</th>
                <th className="text-end">Invoice ₹</th>
                <th>Phone</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {slotOfferBookings.length === 0 && !slotOfferBookingsLoading ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted" style={{ padding: 16 }}>
                    No slot-time offer bookings recorded yet (or run DB migration for payment columns).
                  </td>
                </tr>
              ) : null}
              {slotOfferBookings.map((r) => (
                <tr key={r.eventId ?? r.bookingReferenceId}>
                  <td>
                    <code style={{ color: "#ffd166" }}>{r.bookingReferenceId || "—"}</code>
                  </td>
                  <td className="text-nowrap small">{r.eventDate || "—"}</td>
                  <td className="small">{r.theatreName || "—"}</td>
                  <td className="small">{r.slotTime || "—"}</td>
                  <td>{r.slotTimeOfferId ?? "—"}</td>
                  <td className="text-end">{r.listedSlotBaseInr ?? "—"}</td>
                  <td className="text-end">{r.appliedPackageBaseInr ?? "—"}</td>
                  <td className="text-end text-warning">Rs. {r.packageDiscountInr ?? 0}</td>
                  <td className="text-end">{r.invoiceTotalInr ?? "—"}</td>
                  <td className="small text-nowrap">{r.customerPhone || "—"}</td>
                  <td className="small text-danger" style={{ maxWidth: 220 }}>
                    {r.offerHeadline || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      </div>

      {/* Create / Edit modal */}
      <Modal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        centered
        className="admin-console-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{form.id ? "Edit promo code" : "New promo code"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={onSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code</Form.Label>
              <Form.Control
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. FLASH500"
                required
                maxLength={40}
                autoFocus
              />
              <Form.Text>Customers type this exact code at checkout.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Flash welcome offer"
                maxLength={120}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                maxLength={500}
              />
            </Form.Group>
            <div className="row g-3">
              <Form.Group className="col-6">
                <Form.Label>Discount amount (Rs.)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={form.discountAmount}
                  onChange={(e) => setForm({ ...form, discountAmount: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="col-6">
                <Form.Label>Min. booking amount</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.minBookingAmount}
                  onChange={(e) => setForm({ ...form, minBookingAmount: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-6">
                <Form.Label>Start date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-6">
                <Form.Label>End date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </Form.Group>
            </div>
            <Form.Group className="mb-3 mt-1">
              <Form.Label>Source</Form.Label>
              <Form.Select
                value={form.promoSource || "OTHER"}
                onChange={(e) => setForm({ ...form, promoSource: e.target.value })}
              >
                {PROMO_SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Choosing <strong>Customer popup</strong> ties this row to the welcome popup list. Activating one
                deactivates other customer-popup rows automatically.
              </Form.Text>
            </Form.Group>
            <Form.Check
              className="mt-3"
              type="switch"
              id="promo-active-switch"
              label="Active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <Form.Check
              type="switch"
              id="promo-popup-default-switch"
              label="Show in welcome popup (popup default)"
              checked={form.popupDefault}
              onChange={(e) => setForm({ ...form, popupDefault: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowFormModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save changes" : "Create code"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Usage drawer/modal */}
      <Modal
        show={Boolean(usageOpenFor)}
        onHide={() => setUsageOpenFor(null)}
        centered
        size="lg"
        className="admin-console-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>
            Usage — <code style={{ color: "#ffd166" }}>{usageOpenFor?.code}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usageLoading ? (
            <div className="text-center" style={{ padding: 24 }}>
              <Spinner animation="border" />
            </div>
          ) : usage ? (
            <UsagePanel usage={usage} />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setUsageOpenFor(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm */}
      <Modal
        show={Boolean(confirmDelete)}
        onHide={() => setConfirmDelete(null)}
        centered
        className="admin-console-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Delete promo code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Permanently delete <code style={{ color: "#ffd166" }}>{confirmDelete?.code}</code>?
          Past redemption rows will keep referring to it.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
}

function UsagePanel({ usage }) {
  const code = usage.code || {};
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="row g-3">
        <div className="col-md-4">
          <StatCard label="Leads captured" value={usage.leadCount ?? 0} />
        </div>
        <div className="col-md-4">
          <StatCard label="Redemptions" value={usage.redemptionCount ?? 0} />
        </div>
        <div className="col-md-4">
          <StatCard
            label="Discount given (revenue lost)"
            value={`Rs. ${usage.totalDiscountGiven ?? 0}`}
          />
        </div>
      </div>

      <section>
        <h6 className="text-uppercase" style={{ color: "#ffd166", letterSpacing: "0.12em" }}>
          Recent redemptions
        </h6>
        <Table size="sm" responsive hover variant="dark">
          <thead>
            <tr>
              <th>Phone</th>
              <th>Booking ref</th>
              <th>Event</th>
              <th className="text-end">Discount</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {(usage.recentRedemptions || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted" style={{ padding: 12 }}>
                  No redemptions yet.
                </td>
              </tr>
            ) : null}
            {(usage.recentRedemptions || []).map((r) => (
              <tr key={r.id}>
                <td>{r.phoneNumber || "—"}</td>
                <td>{r.bookingReferenceId || "—"}</td>
                <td>{r.eventId ?? "—"}</td>
                <td className="text-end">Rs. {r.discountAmount}</td>
                <td>{formatDateTime(r.redeemedDate)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>

      <section>
        <h6 className="text-uppercase" style={{ color: "#ffd166", letterSpacing: "0.12em" }}>
          Recent leads
        </h6>
        <Table size="sm" responsive hover variant="dark">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Source</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {(usage.recentLeads || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted" style={{ padding: 12 }}>
                  No leads yet.
                </td>
              </tr>
            ) : null}
            {(usage.recentLeads || []).map((l) => (
              <tr key={l.id}>
                <td>{l.customerName || "—"}</td>
                <td>{l.phoneNumber || "—"}</td>
                <td>{l.email || "—"}</td>
                <td>{l.source || "—"}</td>
                <td>{formatDateTime(l.createdDate)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>

      <div className="text-muted" style={{ fontSize: 12 }}>
        Code id: {code.id} · created {formatDateTime(code.createdDate)} · last modified{" "}
        {formatDateTime(code.modifiedDate)}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        padding: 14,
        background: "rgba(10,6,18,0.75)",
      }}
    >
      <div
        className="text-uppercase"
        style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.16em" }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#ffd166", marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}
